import {
    Box,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import {
    AddFilterMenu,
    Button,
    ClearFiltersButton,
    FilterBar,
    FilterChip,
    Icon,
    MenuFooterActions,
    MenuRow,
    checkboxMenuPopoverContentSx,
} from '../../../ui';
import type { PurchaseOrderStatus } from '../../../types/inventory/purchaseOrders';

/** `all` means no status filter is applied. */
export type PurchaseOrderStatusFilterValue = PurchaseOrderStatus | 'all';

const STATUS_OPTIONS: Array<{ value: PurchaseOrderStatus; label: string }> = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
];

const statusLabel = (value: PurchaseOrderStatus) =>
    STATUS_OPTIONS.find((o) => o.value === value)?.label ?? value;

type OpenState = 'addFilter' | 'status' | null;

export interface PurchaseOrderStatusFilterProps {
    value: PurchaseOrderStatusFilterValue;
    onChange: (value: PurchaseOrderStatusFilterValue) => void;
}

/** Status filter for the PO feed, built on the same FilterBar chrome as InventoryAdvancedFilters. */
export function PurchaseOrderStatusFilter({ value, onChange }: PurchaseOrderStatusFilterProps) {
    const [openState, setOpenState] = useState<OpenState>(null);
    const [isDrilledIn, setIsDrilledIn] = useState(false);
    const [draft, setDraft] = useState<PurchaseOrderStatusFilterValue>(value);
    const containerRef = useRef<HTMLDivElement>(null);

    const close = () => {
        setOpenState(null);
        setIsDrilledIn(false);
    };

    useEffect(() => {
        if (!openState) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            close();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openState]);

    const toggle = (target: Exclude<OpenState, null>) => {
        if (openState === target) {
            close();
            return;
        }
        setDraft(value);
        setIsDrilledIn(false);
        setOpenState(target);
    };

    const apply = () => {
        onChange(draft);
        close();
    };

    const isActive = value !== 'all';

    const editor = (
        <VStack align="stretch" spacing={0} p="6px">
            {STATUS_OPTIONS.map((opt) => (
                <MenuRow
                    key={opt.value}
                    kind="single"
                    isSelected={draft === opt.value}
                    onClick={() => setDraft(opt.value)}
                >
                    {opt.label}
                </MenuRow>
            ))}
        </VStack>
    );

    return (
        <Box ref={containerRef}>
            <FilterBar>
                {isActive ? (
                    <Popover isOpen={openState === 'status'} placement="bottom-start" closeOnBlur={false}>
                        <PopoverTrigger>
                            <FilterChip
                                icon={<Icon name="listCheck" size="md" />}
                                label={`Status: ${statusLabel(value)}`}
                                onRemove={() => onChange('all')}
                                onClick={() => toggle('status')}
                            />
                        </PopoverTrigger>
                        <PopoverContent w="220px" sx={checkboxMenuPopoverContentSx} data-filter-popover>
                            <PopoverBody p={0}>
                                {editor}
                                <MenuFooterActions
                                    secondaryLabel="Cancel"
                                    onSecondary={close}
                                    onApply={apply}
                                />
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                ) : null}

                <AddFilterMenu
                    isOpen={openState === 'addFilter'}
                    onToggle={() => toggle('addFilter')}
                    contentProps={{ 'data-filter-popover': '' }}
                >
                    {isDrilledIn ? (
                        <>
                            <Box px={2} py={2} borderBottom="1px solid" borderColor="border.subtle">
                                <Button
                                    size="xs"
                                    variant="ghostGray"
                                    onClick={() => setIsDrilledIn(false)}
                                    leftIcon={<Icon name="chevronLeft" size="sm" />}
                                    fontWeight="medium"
                                >
                                    Status
                                </Button>
                            </Box>
                            {editor}
                        </>
                    ) : (
                        <VStack align="stretch" spacing={0} p="6px">
                            <MenuRow
                                kind="drillIn"
                                summary={isActive ? statusLabel(value) : undefined}
                                icon={<Icon name="listCheck" size="md" />}
                                onClick={() => setIsDrilledIn(true)}
                            >
                                Status
                            </MenuRow>
                        </VStack>
                    )}
                    <MenuFooterActions onSecondary={() => setDraft('all')} onApply={apply} />
                </AddFilterMenu>

                {isActive ? <ClearFiltersButton onClick={() => onChange('all')} /> : null}
            </FilterBar>
        </Box>
    );
}
